import React,{ Suspense,lazy,useEffect,useCallback } from 'react';
import ScrollToTop from './components/ScrollToTop';
import DefaultOgUrl from './components/DefaultOgUrl';
import ContentShareSlot from './components/ContentShareSlot';
import { Routes,Route,useLocation,Navigate,useParams } from 'react-router-dom';
import { trackPageView,trackError,trackPerformance } from './utils/analytics';
import { AnimatePresence } from 'framer-motion';
import { throttle } from './utils/helpers';
import { ErrorBoundary } from 'react-error-boundary';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CookieConsentLite from './components/CookieConsentLite';
import ErrorFallback from './components/ErrorFallback';
import BackToTop from './components/BackToTop';
import SkipToContent from './components/SkipToContent';
import WebVitalsReporter from './components/WebVitalsReporter';
import PerformanceMonitor from './components/PerformanceMonitor';
import FeedbackRibbon from './components/FeedbackRibbon';
import LoadingSkeleton from './components/LoadingSkeleton';
import { requestIdleCallbackPolyfill } from './utils/requestIdleCallback';
import PerformanceOptimizer from './utils/performanceOptimizations';
import { Chatbot } from './components/Chatbot';
import ExitIntentPopup from './components/ExitIntentPopup';
import ServiceNoticePopup from './components/ServiceNoticePopup';

function WerkgebiedRedirect() {
  const { city } = useParams();
  return <Navigate to={`/airco-installatie/${city ?? ''}`} replace />;
}

import FloatingReviewBadge from './components/FloatingReviewBadge';
import { servicePages } from './data/servicePages';

// Lazy load all pages
const Home = lazy(() => import('./pages/Home'));
const Products = lazy(() => import('./pages/Products'));
const BrandDetail = lazy(() => import('./pages/BrandDetail'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const ScrapedProductDetail = lazy(() => import('./pages/ScrapedProductDetail'));
const Contact = lazy(() => import('./pages/Contact'));
const ServiceArea = lazy(() => import('./pages/ServiceArea'));
const KnowledgeBase = lazy(() => import('./pages/KnowledgeBase'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Over = lazy(() => import('./pages/Over'));
const MaintenanceProcedures = lazy(() => import('./pages/MaintenanceProcedures'));
const AircoCovers = lazy(() => import('./pages/AircoCovers'));
const LGMobieleAircoPage = lazy(() => import('./pages/products/lg-mobiele-airco'));
const TosotMobieleAircoPage = lazy(() => import('./pages/products/tosot-mobiele-airco'));
const TotSnel = lazy(() => import('./pages/TotSnel'));
const ThuisbatterijCalculator = lazy(() => import('./pages/ThuisbatterijCalculator'));
const Thuisbatterijen = lazy(() => import('./pages/Thuisbatterijen'));
const Aircos = lazy(() => import('./pages/Aircos'));

// Naked leadgen landing pages (paid traffic, /lp/ prefix — geen nav/footer)
const AircoInstallatieLP = lazy(() => import('./pages/lp/AircoInstallatieLP'));
const AircoInstallatieLPB = lazy(() => import('./pages/lp/AircoInstallatieLPB'));
const VakantiegeldDealLP = lazy(() => import('./pages/lp/VakantiegeldDealLP'));
const KoelDezeZomerLP = lazy(() => import('./pages/lp/KoelDezeZomerLP'));

// Manual Pages
const LGHandleiding = lazy(() => import('./pages/manuals/LGHandleiding'));
const TosotHandleiding = lazy(() => import('./pages/manuals/TosotHandleiding'));

// Landing Pages
const DaikinLanding = lazy(() => import('./pages/landing/DaikinLanding'));
const LGLanding = lazy(() => import('./pages/landing/LGLanding'));
const MitsubishiHeavyLanding = lazy(() => import('./pages/landing/MitsubishiHeavyLanding'));
const ToshibaLanding = lazy(() => import('./pages/landing/ToshibaLanding'));
const TosotLanding = lazy(() => import('./pages/landing/TosotLanding'));
const MobieleAircoLanding = lazy(() => import('./pages/landing/MobieleAircoLanding'));
const AircoMetBuitenunitLanding = lazy(() => import('./pages/landing/AircoMetBuitenunitLanding'));
const AircoInstallatieLanding = lazy(() => import('./pages/landing/AircoInstallatieLanding'));
const AircoInstallateurLanding = lazy(() => import('./pages/landing/AircoInstallateurLanding'));
const AircoOnderhoudLanding = lazy(() => import('./pages/landing/AircoOnderhoudLanding'));
const AircoBijvullen = lazy(() => import('./pages/AircoBijvullen'));
const AircoPlaatsenLimburg = lazy(() => import('./pages/AircoPlaatsenLimburg'));
const AircoVullenKosten = lazy(() => import('./pages/AircoVullenKosten'));
const AircoBijvullenZelfDoen = lazy(() => import('./pages/articles/AircoBijvullenZelfDoen'));
const AircoService = lazy(() => import('./pages/AircoService'));
const AircoSubsidie = lazy(() => import('./pages/AircoSubsidie'));
const IsdeAanvragenAirco = lazy(() => import('./pages/IsdeAanvragenAirco'));
const AircoWarmtepomp = lazy(() => import('./pages/AircoWarmtepomp'));
const HybrideWarmtepompAirco = lazy(() => import('./pages/HybrideWarmtepompAirco'));
const AircoReparatieLanding = lazy(() => import('./pages/landing/AircoReparatieLanding'));
const AircoLatenPlaatsenLanding = lazy(() => import('./pages/landing/AircoLatenPlaatsenLanding'));
const WatKostAircoInstallatieLanding = lazy(() => import('./pages/landing/WatKostAircoInstallatieLanding'));
const AircoServiceLimburgLanding = lazy(() => import('./pages/landing/AircoServiceLimburgLanding'));
const AircoKopenLimburg = lazy(() => import('./pages/landing/AircoKopenLimburg'));
const AircoKopenEnLatenInstalleren = lazy(() => import('./pages/landing/AircoKopenEnLatenInstalleren'));
const AircoVoorSlaapkamerLanding = lazy(() => import('./pages/landing/AircoVoorSlaapkamerLanding'));
const AircoVoorWoonkamerLanding = lazy(() => import('./pages/landing/AircoVoorWoonkamerLanding'));
const AircoVoorZolderLanding = lazy(() => import('./pages/landing/AircoVoorZolderLanding'));
const KostenAircoPlaatsen = lazy(() => import('./pages/KostenAircoPlaatsen'));
const OnderhoudLanding = lazy(() => import('./pages/OnderhoudLanding'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Comparison Pages
const ComparisonsIndex = lazy(() => import('./pages/comparisons/index'));
const DaikinVsMitsubishiHeavy = lazy(() => import('./pages/comparisons/DaikinVsMitsubishiHeavy'));
const SplitVsMonoblock = lazy(() => import('./pages/comparisons/SplitVsMonoblock'));
const TosotVsLG = lazy(() => import('./pages/comparisons/TosotVsLG'));

// Location Landing Pages — alle 30 cities renderen via één catch-all naar
// ProgrammaticLocationPage, gevoed uit programmaticLocations.ts met
// djb2-hash variant-rotatie (zie data/cityCopyVariants.ts).
const ProgrammaticLocationPage = lazy(() => import('./pages/locations/ProgrammaticLocation'));

// Blog
const BlogPage = lazy(() => import('./pages/Blog'));
const AircoOnderhoudLimburgBelangrijk = lazy(() => import('./pages/blog/AircoOnderhoudLimburgBelangrijk'));
const AircoInstallateurLimburgKiezen = lazy(() => import('./pages/blog/AircoInstallateurLimburgKiezen'));
const AircoOfferteLimburg = lazy(() => import('./pages/blog/AircoOfferteLimburg'));
const AircoSpecialistLimburgBlog = lazy(() => import('./pages/blog/AircoSpecialistLimburg'));
const AircoInstallatieZuidLimburg = lazy(() => import('./pages/blog/AircoInstallatieZuidLimburg'));
const WatKostEenUurAircoDraaien = lazy(() => import('./pages/blog/WatKostEenUurAircoDraaien'));
const AircoSubsidie2026Limburg = lazy(() => import('./pages/blog/AircoSubsidie2026Limburg'));
const BesteAircoMerken2026 = lazy(() => import('./pages/blog/BesteAircoMerken2026'));
const AircoMaastrichtCompleteGids2026 = lazy(() => import('./pages/blog/AircoMaastrichtCompleteGids2026'));
const AircoLevensduurWanneerVervangen = lazy(() => import('./pages/blog/AircoLevensduurWanneerVervangen'));
const AircoStoringenHerkennen = lazy(() => import('./pages/blog/AircoStoringenHerkennen'));
const AircoOfferteMaastricht = lazy(() => import('./pages/blog/AircoOfferteMaastricht'));
const AircoOfferteHeerlen = lazy(() => import('./pages/blog/AircoOfferteHeerlen'));
const AircoOfferteSittard = lazy(() => import('./pages/blog/AircoOfferteSittard'));
const AircoOfferteAanvragenChecklist = lazy(() => import('./pages/blog/AircoOfferteAanvragenChecklist'));
const AircoOfferteOnlineVsInmeting = lazy(() => import('./pages/blog/AircoOfferteOnlineVsInmeting'));
const WaaromVerschillenAircoOffertes = lazy(() => import('./pages/blog/WaaromVerschillenAircoOffertes'));
const MultisplitAircoOfferteWanneerRendabel = lazy(() => import('./pages/blog/MultisplitAircoOfferteWanneerRendabel'));

// Knowledge Base Articles
const HoeWerktAirco = lazy(() => import('./pages/articles/HoeWerktAirco'));
const SoortenAirco = lazy(() => import('./pages/articles/SoortenAirco'));
const SplitVsMonoSystemen = lazy(() => import('./pages/articles/SplitVsMonoSystemen'));
const ScopSeerWaarden = lazy(() => import('./pages/articles/ScopSeerWaarden'));
const CoandaEffect = lazy(() => import('./pages/articles/CoandaEffect'));
const VerwarmenMetAirco = lazy(() => import('./pages/articles/VerwarmenMetAirco'));
const TerugleverKostenVoorkomen = lazy(() => import('./pages/articles/TerugleverKostenVoorkomen'));
const Energielabels = lazy(() => import('./pages/articles/Energielabels'));
const Onderhoudstips = lazy(() => import('./pages/articles/Onderhoudstips'));
const VeelvoorkendeProblemen = lazy(() => import('./pages/articles/VeelvoorkendeProblemen'));
const Onderhoudsschema = lazy(() => import('./pages/articles/Onderhoudsschema'));
const Koudemiddelen = lazy(() => import('./pages/articles/Koudemiddelen'));
const CO2Reductie = lazy(() => import('./pages/articles/CO2Reductie'));
const StoringscodeGids = lazy(() => import('./pages/articles/StoringscodeGids'));
const LGStoringscode = lazy(() => import('./pages/articles/LGStoringscode'));
const ElektrischeVerwarming = lazy(() => import('./pages/articles/ElektrischeVerwarming'));
const VerwarmenMetAircoErvaring = lazy(() => import('./pages/articles/VerwarmenMetAircoErvaring'));
const HoeVaakAircoOnderhoud = lazy(() => import('./pages/articles/HoeVaakAircoOnderhoud'));
const AircoBlaastGeenWarmeLucht = lazy(() => import('./pages/articles/AircoBlaastGeenWarmeLucht'));
const WatKostEenAircoPlaatsen = lazy(() => import('./pages/articles/WatKostEenAircoPlaatsen'));
const AircoBevriesstBijVerwarmen = lazy(() => import('./pages/articles/AircoBevriesstBijVerwarmen'));
const AircoInHerkenbosch = lazy(() => import('./pages/articles/AircoInHerkenbosch'));
const MitsubishiVsLgAirco = lazy(() => import('./pages/articles/MitsubishiVsLgAirco'));
const ToshibaVsTosotAirco = lazy(() => import('./pages/articles/ToshibaVsTosotAirco'));
const AircoLuchtfilterOnderhoud = lazy(() => import('./pages/articles/AircoLuchtfilterOnderhoud'));
const AircoBuitenunitPlaatsing = lazy(() => import('./pages/articles/AircoBuitenunitPlaatsing'));
const AircoLatenInstalleren = lazy(() => import('./pages/articles/AircoLatenInstalleren'));
const MobieleVsSplitAirco = lazy(() => import('./pages/articles/MobieleVsSplitAirco'));
const CapacityCalculator = lazy(() => import('./pages/CapacityCalculator'));
const KnowledgeBaseThuisbatterijen = lazy(() => import('./pages/KnowledgeBaseThuisbatterijen'));

// SEO Articles
const AircoSpecialistLimburg = lazy(() => import('./pages/articles/aircospecialist-limburg'));
const AircoVenlo = lazy(() => import('./pages/articles/airco-venlo'));
const AircoWeert = lazy(() => import('./pages/articles/airco-weert'));
const AircoServiceLimburg = lazy(() => import('./pages/articles/airco-service-limburg'));
const AircoInstallatieLimburg = lazy(() => import('./pages/articles/airco-installatie-limburg'));
const AircoInstallateurLimburg = lazy(() => import('./pages/articles/airco-installateur-limburg'));
const Klimaatbeheersing = lazy(() => import('./pages/articles/klimaatbeheersing'));
const Scop46 = lazy(() => import('./pages/articles/scop-4-6'));
const AircoOnderhoudLimburg = lazy(() => import('./pages/articles/airco-onderhoud-limburg'));
const AircoInstallerenLimburg = lazy(() => import('./pages/articles/airco-installeren-limburg'));
const AircoInstallateurRoermond = lazy(() => import('./pages/articles/airco-installateur-roermond'));
const AircoInstallateurMaastricht = lazy(() => import('./pages/articles/airco-installateur-maastricht'));
const AircoBrunssum = lazy(() => import('./pages/articles/airco-brunssum'));
const AircoSittard = lazy(() => import('./pages/articles/airco-sittard'));
const MitsubishiAircoLimburg = lazy(() => import('./pages/articles/mitsubishi-airco-limburg'));
const AirconditioningKerkrade = lazy(() => import('./pages/articles/airconditioning-kerkrade'));
const EnergiezuinigeAircoLimburg = lazy(() => import('./pages/articles/energiezuinige-airco-limburg'));
const AircoVerwarmingKostenBesparing = lazy(() => import('./pages/AircoVerwarmingKostenBesparing'));
const AircoVerwarmingVoordelen = lazy(() => import('./pages/articles/airco-verwarming-voordelen'));

// New SEO Funnel - BOFU Money Pages
const AircoInstallatiePrijs = lazy(() => import('./pages/landing/AircoInstallatiePrijs'));
const AircoOfferte = lazy(() => import('./pages/landing/AircoOfferte'));
const SplitAircoInstallatie = lazy(() => import('./pages/landing/SplitAircoInstallatie'));
const MultiSplitAircoInstallatie = lazy(() => import('./pages/landing/MultiSplitAircoInstallatie'));

// New SEO Funnel - MOFU Comparison/Decision Pages
const BesteAircoVoorVerwarmen = lazy(() => import('./pages/landing/BesteAircoVoorVerwarmen'));
const Airco35KwHoevelM2 = lazy(() => import('./pages/landing/Airco35KwHoevelM2'));
const AircoSlaapkamerWelkeKw = lazy(() => import('./pages/landing/AircoSlaapkamerWelkeKw'));
const AircoKoelenEnVerwarmen = lazy(() => import('./pages/landing/AircoKoelenEnVerwarmen'));
const AircoMetWifiVoordelen = lazy(() => import('./pages/landing/AircoMetWifiVoordelen'));
const AircoAPlusPlusVerwarmen = lazy(() => import('./pages/landing/AircoAPlusPlusVerwarmen'));
const AircoMetBuitenunitOfZonder = lazy(() => import('./pages/landing/AircoMetBuitenunitOfZonder'));
const BesteAircoMerken = lazy(() => import('./pages/landing/BesteAircoMerken'));
const AirconditioningPillar = lazy(() => import('./pages/landing/AirconditioningPillar'));
const LGVsDaikinAirco = lazy(() => import('./pages/comparisons/LGVsDaikinAirco'));

// New SEO Funnel - Room Landing Pages
const AircoKantoor = lazy(() => import('./pages/landing/AircoKantoor'));
const AircoWinkel = lazy(() => import('./pages/landing/AircoWinkel'));
const ServiceLandingPage = lazy(() => import('./pages/ServiceLandingPage'));

// New SEO Funnel - TOFU Articles
const AircoAlsVerwarmingKosten = lazy(() => import('./pages/articles/airco-als-verwarming-kosten'));
const WatKostAircoPerMaand = lazy(() => import('./pages/articles/wat-kost-airco-per-maand'));
const IsAircoGoedkoperDanGas = lazy(() => import('./pages/articles/is-airco-goedkoper-dan-gas'));
const AircoGeluidBurenRegels = lazy(() => import('./pages/articles/airco-geluid-buren-regels'));
const AircoVsWarmtepomp = lazy(() => import('./pages/articles/airco-vs-warmtepomp'));
const AircoVoordelenEnNadelen = lazy(() => import('./pages/articles/airco-voordelen-en-nadelen'));
const AircoInstallatieDuur = lazy(() => import('./pages/articles/airco-installatie-duur'));
const AircoInstallerenGids = lazy(() => import('./pages/landing/AircoInstallerenGids'));

// Programmatic Ruimte × Stad (135 pages: 5 rooms × 27 cities)
const ProgrammaticRuimteStadPage = lazy(() => import('./pages/locations/ProgrammaticRuimteStad'));

// SEO Landing Pages - Pillar 1: Installatie Services
const SEOAircoInstallatieWoonkamer = lazy(() => import('./pages/seo/pillar-1-installatie-services/airco-installatie-woonkamer'));
const SEOAircoInstallatieSlaapkamer = lazy(() => import('./pages/seo/pillar-1-installatie-services/airco-installatie-slaapkamer'));
const SEOAircoInstallatieKantoor = lazy(() => import('./pages/seo/pillar-1-installatie-services/airco-installatie-kantoor'));
const SEOAircoInstallatieBedrijfspand = lazy(() => import('./pages/seo/pillar-1-installatie-services/airco-installatie-bedrijfspand'));
const SEOMultiSplitAircoInstallatie = lazy(() => import('./pages/seo/pillar-1-installatie-services/multi-split-airco-installatie'));

// SEO Landing Pages - Pillar 2: Onderhoud & Service
const SEOJaarlijksAircoOnderhoud = lazy(() => import('./pages/seo/pillar-2-onderhoud-service/jaarlijks-airco-onderhoud'));
const SEOAircoReparatieService = lazy(() => import('./pages/seo/pillar-2-onderhoud-service/airco-reparatie-service'));
const SEOAircoFilterReiniging = lazy(() => import('./pages/seo/pillar-2-onderhoud-service/airco-filter-reiniging'));
const SEOAircoStoringOplossen = lazy(() => import('./pages/seo/pillar-2-onderhoud-service/airco-storing-oplossen'));

// SEO Landing Pages - Pillar 3: Merken & Modellen
const SEODaikinAircoModellen = lazy(() => import('./pages/seo/pillar-3-merken-modellen/daikin-airco-modellen'));
const SEOMitsubishiHeavyAirco = lazy(() => import('./pages/seo/pillar-3-merken-modellen/mitsubishi-heavy-airco'));
const SEOLGAircoSystemen = lazy(() => import('./pages/seo/pillar-3-merken-modellen/lg-airco-systemen'));
const SEOTosotGreeAirco = lazy(() => import('./pages/seo/pillar-3-merken-modellen/tosot-gree-airco'));
const SEOSamsungAirco = lazy(() => import('./pages/seo/pillar-3-merken-modellen/samsung-airco'));

// SEO Landing Pages - Pillar 4: Energie Besparen
const SEOVerwarmenMetAirco = lazy(() => import('./pages/seo/pillar-4-energie-besparen/verwarmen-met-airco'));
const SEOTerugleverkostenVoorkomen = lazy(() => import('./pages/seo/pillar-4-energie-besparen/terugleverkosten-voorkomen'));
const SEOSCOPWaardeUitgelegd = lazy(() => import('./pages/seo/pillar-4-energie-besparen/scop-waarde-uitgelegd'));
const SEOEnergielabelAirco = lazy(() => import('./pages/seo/pillar-4-energie-besparen/energielabel-airco'));
const SEOStroomverbruikAirco = lazy(() => import('./pages/seo/pillar-4-energie-besparen/stroomverbruik-airco'));

// SEO Landing Pages - Pillar 5: AlphaESS Thuisbatterijen
const SEOThuisbatterijSMILEB3 = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-smile-b3'));
const SEOThuisbatterijSMILE5 = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-smile5'));
const SEOThuisbatterijSMILET10 = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-smile-t10'));
const SEOThuisbatterijBTWTeruggave = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-btw-teruggave'));
const SEOThuisbatterijTerugverdientijd = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-terugverdientijd'));
const SEOThuisbatterijAlphaESSVsConcurrenten = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-alphaess-vs-concurrenten'));
const SEOThuisbatterijKopenOfHuren = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-kopen-of-huren'));
const SEOThuisbatterijDynamischEnergiecontract = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-dynamisch-energiecontract'));
const SEOThuisbatterijZonnepanelen = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-zonnepanelen'));
const SEOThuisbatterijInstallatieLimburg = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-installatie-limburg'));
const SEOThuisbatterij1FaseVs3Fase = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-1-fase-vs-3-fase'));
const SEOThuisbatterijCapaciteitKiezen = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-capaciteit-kiezen'));
const SEOThuisbatterijKostenOverzicht = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-kosten-overzicht'));
const SEOThuisbatterijSubsidieLimburg = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-subsidie-limburg'));
const SEOThuisbatterijNoodstroom = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-noodstroom'));
const SEOThuisbatterijElektrischeAuto = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-elektrische-auto'));
const SEOThuisbatterijOnderhoud = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-onderhoud'));
const SEOThuisbatterijLevensduurGarantie = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-levensduur-garantie'));
const SEOThuisbatterijWarmtepomp = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-warmtepomp'));
const SEOThuisbatterijSmartHome = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-smart-home-integratie'));
const SEOThuisbatterijNieuwbouw = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-nieuwbouw'));
const SEOThuisbatterijSaldering2027 = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-saldering-2027'));
const SEOThuisbatterijZakelijkMKB = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-zakelijk-mkb'));
const SEOThuisbatterijMonitoringApp = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-monitoring-app'));
const SEOThuisbatterijVerzekering = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-verzekering'));
const SEOThuisbatterijFinancieren = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-financieren'));
const SEOThuisbatterijAansluitenMeterkast = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-aansluiten-meterkast'));
const SEOThuisbatterijBesparingBerekenen = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-besparing-berekenen'));
const SEOThuisbatterijHurenVsKopen = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-huren-vs-kopen'));
const SEOThuisbatterijSubsidie2026 = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-subsidie-2026'));
const SEOThuisbatterijVsTeslaPowerwall = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-vs-tesla-powerwall'));
const SEOThuisbatterijVsWaterstof = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-vs-waterstof'));
const SEOAlphaESSVsHuaweiLuna = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/alphaess-vs-huawei-luna'));
const SEOThuisbatterijMaastricht = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-maastricht'));
const SEOThuisbatterijRoermond = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-roermond'));
const SEOThuisbatterijHeerlen = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-heerlen'));
const SEOThuisbatterijVenlo = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-venlo'));
const SEOThuisbatterijSittardGeleen = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/thuisbatterij-sittard-geleen'));

// SEO Advice Pages ("Best of")
const AdviesIndex = lazy(() => import('./pages/AdviesIndex'));
const BesteAircoSlaapkamer = lazy(() => import('./pages/advies/BesteAircoSlaapkamer'));
const Top10StilleAircos = lazy(() => import('./pages/advies/Top10StilleAircos'));
const Top5EnergiezuinigeAircos = lazy(() => import('./pages/advies/Top5EnergiezuinigeAircos'));
const BesteMultiSplitAircos = lazy(() => import('./pages/advies/BesteMultiSplitAircos'));

// Noindex thuisbatterij pages
const SEOThuisbatterijErvaringenKlanten = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/noindex/thuisbatterij-ervaringen-klanten'));
const SEOThuisbatterijGidsLimburg = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/noindex/thuisbatterij-gids-limburg'));
const SEOThuisbatterijHulpKiezen = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/noindex/thuisbatterij-hulp-kiezen'));
const SEOThuisbatterijInstallateursRegio = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/noindex/thuisbatterij-installateurs-regio'));
const SEOThuisbatterijInstallerenStappen = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/noindex/thuisbatterij-installeren-stappen'));
const SEOThuisbatterijLokaleRegelgeving = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/noindex/thuisbatterij-lokale-regelgeving'));
const SEOThuisbatterijMerkenVergelijken = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/noindex/thuisbatterij-merken-vergelijken'));
const SEOThuisbatterijPrijzen2025 = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/noindex/thuisbatterij-prijzen-2025'));
const SEOThuisbatterijVeelgesteldeVragen = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/noindex/thuisbatterij-veelgestelde-vragen'));
const SEOThuisbatterijVoorHuurders = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/noindex/thuisbatterij-voor-huurders'));
const SEOThuisbatterijVoorSenioren = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/noindex/thuisbatterij-voor-senioren'));
const SEOThuisbatterijVoorZelfstandigen = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/noindex/thuisbatterij-voor-zelfstandigen'));
const SEOThuisbatterijVragenAntwoordenLimburg = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/noindex/thuisbatterij-vragen-antwoorden-limburg'));
const SEOThuisbatterijWanneerRendabel = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/noindex/thuisbatterij-wanneer-rendabel'));
const SEOThuisbatterijWerkingUitgelegd = lazy(() => import('./pages/seo/pillar-5-thuisbatterijen/noindex/thuisbatterij-werking-uitgelegd'));

// Noindex airco installatie limburg pages
const SEOAircoInstallatieLimburgStappenplan = lazy(() => import('./pages/seo/pillar-1-airco-installatie/noindex/airco-installatie-limburg-stappenplan'));
const SEOAircoInstallatieLimburgKosten = lazy(() => import('./pages/seo/pillar-1-airco-installatie/noindex/airco-installatie-limburg-kosten'));
const SEOAircoInstallatieLimburgFAQ = lazy(() => import('./pages/seo/pillar-1-airco-installatie/noindex/airco-installatie-limburg-faq'));

// Noindex energiezuinige airco pages
const SEOEnergiezuinigeAircoKiezen = lazy(() => import('./pages/seo/pillar-3-energiebesparing/noindex/energiezuinige-airco-kiezen'));
const SEOAircoEnergielabelUitgelegd = lazy(() => import('./pages/seo/pillar-3-energiebesparing/noindex/airco-energielabel-uitgelegd'));
const SEOAircoStroomverbruikBerekenen = lazy(() => import('./pages/seo/pillar-3-energiebesparing/noindex/airco-stroomverbruik-berekenen'));

// Noindex airco onderhoud pages
const SEOAircoOnderhoudChecklist = lazy(() => import('./pages/seo/pillar-2-onderhoud-service/noindex/airco-onderhoud-checklist'));
const SEOAircoOnderhoudSeizoenen = lazy(() => import('./pages/seo/pillar-2-onderhoud-service/noindex/airco-onderhoud-seizoenen'));
const SEOAircoOnderhoudProblemenVoorkomen = lazy(() => import('./pages/seo/pillar-2-onderhoud-service/noindex/airco-onderhoud-problemen-voorkomen'));

// Noindex airco reparatie pages
const SEOAircoReparatieStoringen = lazy(() => import('./pages/seo/pillar-2-onderhoud-service/noindex/airco-reparatie-storingen'));
const SEOAircoReparatieKostenLimburg = lazy(() => import('./pages/seo/pillar-2-onderhoud-service/noindex/airco-reparatie-kosten-limburg'));
const SEOAircoReparatieSpoed = lazy(() => import('./pages/seo/pillar-2-onderhoud-service/noindex/airco-reparatie-spoed'));

// Noindex airco service limburg pages
const SEOAircoServiceWatVerwachten = lazy(() => import('./pages/seo/pillar-2-onderhoud-service/noindex/airco-service-wat-verwachten'));
const SEOAircoServiceContractVoordelen = lazy(() => import('./pages/seo/pillar-2-onderhoud-service/noindex/airco-service-contract-voordelen'));
const SEOAircoServiceLimburgRegio = lazy(() => import('./pages/seo/pillar-2-onderhoud-service/noindex/airco-service-limburg-regio'));

// Noindex airco merken pages
const SEOMerkenKeuzehulp = lazy(() => import('./pages/seo/pillar-3-merken-modellen/noindex/airco-merken-keuzehulp'));
const SEOModellenOverzicht2025 = lazy(() => import('./pages/seo/pillar-3-merken-modellen/noindex/airco-modellen-overzicht-2025'));
const SEOMerkVergelijkingTips = lazy(() => import('./pages/seo/pillar-3-merken-modellen/noindex/airco-merk-vergelijking-tips'));

// SEO Landing Pages - Pillar 6: Limburg Steden
const SEOAircoInstallatieMaastricht = lazy(() => import('./pages/seo/pillar-6-limburg-steden/airco-installatie-maastricht'));
const SEOAircoInstallatieRoermond = lazy(() => import('./pages/seo/pillar-6-limburg-steden/airco-installatie-roermond'));
const SEOAircoServiceHeerlen = lazy(() => import('./pages/seo/pillar-6-limburg-steden/airco-service-heerlen'));
const SEOAircoMonteurSittardGeleen = lazy(() => import('./pages/seo/pillar-6-limburg-steden/airco-monteur-sittard-geleen'));
const SEOAircoPlaatsenVenlo = lazy(() => import('./pages/seo/pillar-6-limburg-steden/airco-plaatsen-venlo'));
const SEOAircoInstallatieWeert = lazy(() => import('./pages/seo/pillar-6-limburg-steden/airco-installatie-weert'));
const SEOAircoInstallatieKerkrade = lazy(() => import('./pages/seo/pillar-6-limburg-steden/airco-installatie-kerkrade'));
const SEOAircoInstallatieEchtSusteren = lazy(() => import('./pages/seo/pillar-6-limburg-steden/airco-installatie-echt-susteren'));

// Noindex Limburg Steden pages
const SEOLimburgWijkgids = lazy(() => import('./pages/seo/pillar-6-limburg-steden/noindex/airco-limburg-wijkgids'));
const SEOLimburgGemeentenOverzicht = lazy(() => import('./pages/seo/pillar-6-limburg-steden/noindex/airco-limburg-gemeenten-overzicht'));
const SEOLimburgReistijdService = lazy(() => import('./pages/seo/pillar-6-limburg-steden/noindex/airco-limburg-reistijd-service'));

// SEO Landing Pages - Pillar 7: Technische Gidsen
const SEOAircoCapaciteitBerekenen = lazy(() => import('./pages/seo/pillar-7-technische-gidsen/airco-capaciteit-berekenen'));
const SEOAircoBuitenunitPlaatsing = lazy(() => import('./pages/seo/pillar-7-technische-gidsen/airco-buitenunit-plaatsing'));
const SEOAircoBinnenunitPositie = lazy(() => import('./pages/seo/pillar-7-technische-gidsen/airco-binnenunit-positie'));
const SEOAircoLeidingwerk = lazy(() => import('./pages/seo/pillar-7-technische-gidsen/airco-leidingwerk'));
const SEOAircoWiFiBediening = lazy(() => import('./pages/seo/pillar-7-technische-gidsen/airco-wifi-bediening'));

// SEO Landing Pages - Pillar 7: Technische Gidsen (NOINDEX)
const SEOTechnischeSpecificaties = lazy(() => import('./pages/seo/pillar-7-technische-gidsen/noindex/airco-technische-specificaties-uitleg'));
const SEOCapaciteitBerekeningTool = lazy(() => import('./pages/seo/pillar-7-technische-gidsen/noindex/airco-capaciteit-berekening-tool'));
const SEOInstallatieTechnischeEisen = lazy(() => import('./pages/seo/pillar-7-technische-gidsen/noindex/airco-installatie-technische-eisen'));

// SEO Landing Pages - Pillar 8: Vergelijkingen
const SEOSplitUnitVsMobieleAirco = lazy(() => import('./pages/seo/pillar-8-vergelijkingen/split-unit-vs-mobiele-airco'));
const SEOMonoSplitVsMultiSplit = lazy(() => import('./pages/seo/pillar-8-vergelijkingen/mono-split-vs-multi-split'));
const SEOInverterVsOnOffAirco = lazy(() => import('./pages/seo/pillar-8-vergelijkingen/inverter-vs-on-off-airco'));
const SEODaikinVsMitsubishi = lazy(() => import('./pages/seo/pillar-8-vergelijkingen/daikin-vs-mitsubishi'));
const SEOAircoVsVentilator = lazy(() => import('./pages/seo/pillar-8-vergelijkingen/airco-vs-ventilator'));

// SEO Landing Pages - Pillar 8: Vergelijkingen (NOINDEX)
const SEOTypesVergelijkenGids = lazy(() => import('./pages/seo/pillar-8-vergelijkingen/noindex/airco-types-vergelijken-gids'));
const SEOSplitVsMobielWatKiezen = lazy(() => import('./pages/seo/pillar-8-vergelijkingen/noindex/split-vs-mobiel-wat-kiezen'));
const SEOAircoVergelijkingChecklist = lazy(() => import('./pages/seo/pillar-8-vergelijkingen/noindex/airco-vergelijking-checklist'));

// SEO Landing Pages - Pillar 9: Kosten & Prijzen
const SEOKostenAircoPlaatsen = lazy(() => import('./pages/seo/pillar-9-kosten-prijzen/kosten-airco-plaatsen'));
const SEOAircoSubsidiesLimburg = lazy(() => import('./pages/seo/pillar-9-kosten-prijzen/airco-subsidies-limburg'));
const SEOAircoFinanciering = lazy(() => import('./pages/seo/pillar-9-kosten-prijzen/airco-financiering'));
const SEOAircoOnderhoudKosten = lazy(() => import('./pages/seo/pillar-9-kosten-prijzen/airco-onderhoud-kosten'));
const SEOAircoVerbruikskosten = lazy(() => import('./pages/seo/pillar-9-kosten-prijzen/airco-verbruikskosten'));

// SEO Landing Pages - Pillar 9: Kosten & Prijzen (NOINDEX)
const SEOAircoPrijzenVergelijken = lazy(() => import('./pages/seo/pillar-9-kosten-prijzen/noindex/airco-prijzen-vergelijken'));
const SEOAircoKostenBesparenTips = lazy(() => import('./pages/seo/pillar-9-kosten-prijzen/noindex/airco-kosten-besparen-tips'));
const SEOAircoInvesteringTerugverdienen = lazy(() => import('./pages/seo/pillar-9-kosten-prijzen/noindex/airco-investering-terugverdienen'));

// SEO Landing Pages - Pillar 10: Duurzaamheid & Milieu
const SEOKoudemiddelR32 = lazy(() => import('./pages/seo/pillar-10-duurzaamheid/koudemiddel-r32'));
const SEOCO2ReductieWarmtepomp = lazy(() => import('./pages/seo/pillar-10-duurzaamheid/co2-reductie-warmtepomp'));
const SEODuurzameVerwarming = lazy(() => import('./pages/seo/pillar-10-duurzaamheid/duurzame-verwarming'));
const SEOEnergielabelVerbetering = lazy(() => import('./pages/seo/pillar-10-duurzaamheid/energielabel-verbetering'));
const SEOCirculaireAirco = lazy(() => import('./pages/seo/pillar-10-duurzaamheid/circulaire-airco'));

// SEO Landing Pages - Pillar 10: Duurzaamheid & Milieu (NOINDEX)
const SEODuurzameAircoGids = lazy(() => import('./pages/seo/pillar-10-duurzaamheid/noindex/duurzame-airco-keuze-gids'));
const SEOMilieuvriendelijkeKoelingTips = lazy(() => import('./pages/seo/pillar-10-duurzaamheid/noindex/milieuvriendelijke-koeling-tips'));
const SEOAircoCO2FootprintVerlagen = lazy(() => import('./pages/seo/pillar-10-duurzaamheid/noindex/airco-co2-footprint-verlagen'));

// Loading fallback component with timeout and auto-reload


// More efficient preloading with requestIdleCallback
const preloadCriticalRoutes = () => {
  const criticalRoutes = [
    () => import('./pages/Home'),
    () => import('./pages/Products'),
    () => import('./pages/Contact')
  ];

  // Use requestIdleCallback polyfill for better timing
  requestIdleCallbackPolyfill(() => {
    criticalRoutes.forEach(route => route());
  }, { timeout: 2000 });
};

const App = () => {
  const location = useLocation();

  // Naked layout voor betaalde landingspagina's: alle routes onder /lp/
  // krijgen geen navbar/footer/chat/popups (één conversiedoel, geen leaks).
  const isBareLayout = location.pathname.startsWith('/lp/');

  // Initialize performance optimizations
  useEffect(() => {
    PerformanceOptimizer.initializeOptimizations();
  }, []);

  // Throttled tracking function to avoid blocking main thread
  const throttledTrackPageView = useCallback(
    throttle((path: string) => {
      trackPageView(path);
    }, 100),
    []
  );

  // Track page views with throttling
  useEffect(() => {
    // Delay tracking until after animation completes
    setTimeout(() => {
      throttledTrackPageView(location.pathname);
    }, 100);
  }, [location, throttledTrackPageView]);

  // Optimized performance metrics tracking with passive listeners
  useEffect(() => {
    const reportWebVitals = ({ name, value, rating }: { name: string; value: number; rating?: 'good' | 'needs-improvement' | 'poor' }) => {
      // Use requestIdleCallback polyfill to report metrics during idle time
      requestIdleCallbackPolyfill(() => {
        trackPerformance({ name, value, rating });
      });
    };

    // Listen for web vitals
    if ('web-vital' in window) {
      (window as any)['web-vital'].getCLS(reportWebVitals);
      (window as any)['web-vital'].getFID(reportWebVitals);
      (window as any)['web-vital'].getLCP(reportWebVitals);
      (window as any)['web-vital'].getFCP(reportWebVitals);
      (window as any)['web-vital'].getTTFB(reportWebVitals);
    }
  }, []);

  // Memoized error boundary handler
  const handleError = useCallback((error: Error) => {
    // Avoid blocking the main thread during error handling
    setTimeout(() => {
      trackError('react_error_boundary', error.message);
    }, 0);
  }, []);

  // Preload critical routes after initial render
  useEffect(() => {
    preloadCriticalRoutes();
  }, []);

  // Smart preloading with requestIdleCallback to avoid blocking the main thread
  useEffect(() => {
    const path = location.pathname;

    const preloadRoutes = () => {
      if (path === '/') {
        return [
          () => import('./pages/Products'),
          () => import('./pages/Contact')
        ];
      } else if (path.startsWith('/products')) {
        return [
          () => import('./pages/ProductDetail')
          // Removed dynamic import of ProductCarousel since it's statically imported elsewhere
        ];
      } else if (path.startsWith('/kennisbank')) {
        return [
          () => import('./pages/articles/HoeWerktAirco'),
          () => import('./pages/articles/SoortenAirco')
        ];
      }
      return [];
    };

    const routes = preloadRoutes();

    if (routes.length > 0) {
      requestIdleCallbackPolyfill(() => {
        routes.forEach(route => route());
      }, { timeout: 1500 });
    }
  }, [location]);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleError}>
      <WebVitalsReporter />
      <div className="min-h-screen bg-white">
        <ScrollToTop />
        <DefaultOgUrl />
        <SkipToContent />
        {!isBareLayout && <Navbar />}
        <main id="main-content" tabIndex={-1}>
          {/* Removed "wait" mode to improve performance - no need to wait for exit animations */}
          <AnimatePresence mode="sync" initial={false}>
            <Suspense fallback={<LoadingSkeleton />}>
              <Routes>
                <Route path="/products/mitsubishi/daiseikai-10" element={<Navigate to="/products/toshiba/daiseikai-10" replace />} />
                <Route path="/products/lg-mobiele-airco" element={<LGMobieleAircoPage />} />
                <Route path="/products/tosot-mobiele-airco" element={<TosotMobieleAircoPage />} />
                <Route path="/" element={<Home />} />
                <Route path="/lp/airco-installatie" element={<AircoInstallatieLP />} />
                <Route path="/lp/airco-installatie-b" element={<AircoInstallatieLPB />} />
                <Route path="/lp/vakantiegeld-deal" element={<VakantiegeldDealLP />} />
                <Route path="/lp/koel-deze-zomer" element={<KoelDezeZomerLP />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/airco-covers" element={<AircoCovers />} />
                <Route path="/products/airco/:productId" element={<ScrapedProductDetail />} />
                <Route path="/products/:brand" element={<BrandDetail />} />
                <Route path="/products/:brand/:model" element={<ProductDetail />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/tot-snel" element={<TotSnel />} />
                <Route path="/werkgebied" element={<ServiceArea />} />
                <Route path="/werkgebied/:city" element={<WerkgebiedRedirect />} />
                <Route path="/over" element={<Over />} />
                <Route path="/about" element={<Navigate to="/over" replace />} />
                <Route path="/vacatures" element={<Navigate to="/contact" replace />} />
                <Route path="/cookies" element={<Navigate to="/privacy" replace />} />
                <Route path="/seo/pillar-1-installatie-services" element={<Navigate to="/seo/pillar-1-installatie-services/airco-installatie-woonkamer" replace />} />
                <Route path="/seo/pillar-2-onderhoud-service" element={<Navigate to="/seo/pillar-2-onderhoud-service/jaarlijks-airco-onderhoud" replace />} />
                <Route path="/seo/pillar-3-merken-modellen" element={<Navigate to="/seo/pillar-3-merken-modellen/daikin-airco-modellen" replace />} />
                <Route path="/seo/pillar-4-energie-besparen" element={<Navigate to="/seo/pillar-4-energie-besparen/verwarmen-met-airco" replace />} />
                <Route path="/seo/pillar-5-thuisbatterijen" element={<Navigate to="/seo/pillar-5-thuisbatterijen/thuisbatterij-smile-b3" replace />} />
                <Route path="/seo/pillar-6-limburg-steden" element={<Navigate to="/seo/pillar-6-limburg-steden/airco-installatie-maastricht" replace />} />
                <Route path="/seo/pillar-7-technische-gidsen" element={<Navigate to="/seo/pillar-7-technische-gidsen/airco-capaciteit-berekenen" replace />} />
                <Route path="/seo/pillar-8-vergelijkingen" element={<Navigate to="/seo/pillar-8-vergelijkingen/split-unit-vs-mobiele-airco" replace />} />
                <Route path="/seo/pillar-9-kosten-prijzen" element={<Navigate to="/kosten-airco-plaatsen" replace />} />
                <Route path="/seo/pillar-10-duurzaamheid" element={<Navigate to="/seo/pillar-10-duurzaamheid/koudemiddel-r32" replace />} />
                <Route path="/kennisbank/subsidies" element={<Navigate to="/seo/pillar-9-kosten-prijzen/airco-subsidies-limburg" replace />} />
                <Route path="/kennisbank/airconditioning-limburg" element={<Navigate to="/kennisbank/airco-installatie-limburg" replace />} />
                <Route path="/kennisbank/airco-monteur-limburg" element={<Navigate to="/kennisbank/airco-installateur-limburg" replace />} />
                <Route path="/kennisbank/klimaatbeheersingssysteem" element={<Navigate to="/kennisbank/klimaatbeheersing" replace />} />
                <Route path="/kennisbank/airco-maastricht" element={<Navigate to="/kennisbank/airco-installateur-maastricht" replace />} />
                <Route path="/kennisbank/airco-heerlen" element={<Navigate to="/airco-installatie/heerlen" replace />} />
                <Route path="/kennisbank/airco-roermond" element={<Navigate to="/kennisbank/airco-installateur-roermond" replace />} />
                <Route path="/kennisbank/airco-installatie-venray" element={<Navigate to="/airco-installatie/venray" replace />} />
                <Route path="/kennisbank/airco-bedrijven-limburg" element={<Navigate to="/seo/pillar-1-installatie-services/airco-installatie-bedrijfspand" replace />} />
                <Route path="/zakelijk" element={<Navigate to="/seo/pillar-1-installatie-services/airco-installatie-bedrijfspand" replace />} />
                <Route path="/merken" element={<Navigate to="/products" replace />} />
                <Route path="/reviews" element={<Navigate to="/" replace />} />
                <Route path="/gratis-onderhoud" element={<Navigate to="/airco-onderhoud-limburg" replace />} />
                <Route path="/besparingcalculator" element={<Navigate to="/thuisbatterij-calculator" replace />} />
                <Route path="/airconditioners" element={<Navigate to="/aircos" replace />} />
                <Route path="/capacity-calculator" element={<Navigate to="/capaciteit-calculator" replace />} />
                <Route path="/products/airco-bescherming/omkasting/:slug" element={<Navigate to="/products/airco-covers" replace />} />
                <Route path="/kennisbank" element={<KnowledgeBase />} />
                <Route path="/aircos" element={<Aircos />} />
                <Route path="/aircos/:productId" element={<ScrapedProductDetail />} />
                <Route path="/thuisbatterijen" element={<Thuisbatterijen />} />
                <Route path="/thuisbatterij-calculator" element={<ThuisbatterijCalculator />} />
                <Route path="/voorwaarden" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/onderhoud" element={<MaintenanceProcedures />} />

                {/* Comparison Pages */}
                <Route path="/vergelijkingen" element={<ComparisonsIndex />} />
                <Route path="/vergelijkingen/daikin-vs-mitsubishi-heavy" element={<DaikinVsMitsubishiHeavy />} />
                <Route path="/vergelijkingen/split-vs-monoblock" element={<SplitVsMonoblock />} />
                <Route path="/vergelijkingen/tosot-vs-lg" element={<TosotVsLG />} />

                {/* Manual Pages */}
                <Route path="/handleidingen/lg" element={<LGHandleiding />} />
                <Route path="/handleidingen/tosot" element={<TosotHandleiding />} />

                {/* Knowledge Base Articles */}
                <Route path="/kennisbank/hoe-werkt-airco" element={<HoeWerktAirco />} />
                <Route path="/kennisbank/soorten-airco" element={<SoortenAirco />} />
                <Route path="/kennisbank/split-vs-mono-systemen" element={<SplitVsMonoSystemen />} />
                <Route path="/kennisbank/scop-seer-waarden" element={<ScopSeerWaarden />} />
                <Route path="/kennisbank/coanda-effect" element={<CoandaEffect />} />
                <Route path="/kennisbank/verwarmen-met-airco" element={<VerwarmenMetAirco />} />
                <Route path="/kennisbank/terugleverkosten-voorkomen" element={<TerugleverKostenVoorkomen />} />
                <Route path="/kennisbank/energielabels" element={<Energielabels />} />
                <Route path="/kennisbank/onderhoudstips" element={<Onderhoudstips />} />
                <Route path="/kennisbank/veelvoorkomende-problemen" element={<VeelvoorkendeProblemen />} />
                <Route path="/kennisbank/onderhoudsschema" element={<Onderhoudsschema />} />
                <Route path="/kennisbank/koudemiddelen" element={<Koudemiddelen />} />
                <Route path="/kennisbank/co2-reductie" element={<CO2Reductie />} />
                <Route path="/kennisbank/storingscodes" element={<StoringscodeGids />} />
                <Route path="/lg-airco-storingscodes" element={<LGStoringscode />} />
                <Route path="/kennisbank/elektrische-verwarming" element={<ElektrischeVerwarming />} />
                <Route path="/kennisbank/verwarmen-met-airco-ervaring" element={<VerwarmenMetAircoErvaring />} />
                <Route path="/kennisbank/hoe-vaak-airco-onderhoud" element={<HoeVaakAircoOnderhoud />} />
                <Route path="/kennisbank/airco-blaast-geen-warme-lucht" element={<AircoBlaastGeenWarmeLucht />} />
                <Route path="/kennisbank/wat-kost-een-airco-plaatsen" element={<WatKostEenAircoPlaatsen />} />
                <Route path="/kennisbank/airco-bevriest-bij-verwarmen" element={<AircoBevriesstBijVerwarmen />} />
                <Route path="/kennisbank/airco-in-herkenbosch" element={<AircoInHerkenbosch />} />
                <Route path="/kennisbank/mitsubishi-vs-lg-airco" element={<MitsubishiVsLgAirco />} />
                <Route path="/kennisbank/toshiba-vs-tosot-airco" element={<ToshibaVsTosotAirco />} />
                <Route path="/kennisbank/airco-luchtfilter-onderhoud" element={<AircoLuchtfilterOnderhoud />} />
                <Route path="/kennisbank/airco-buitenunit-plaatsing" element={<AircoBuitenunitPlaatsing />} />
                <Route path="/kennisbank/airco-laten-installeren" element={<AircoLatenInstalleren />} />
                <Route path="/kennisbank/mobiele-vs-split-airco" element={<MobieleVsSplitAirco />} />
                <Route path="/capaciteit-calculator" element={<CapacityCalculator />} />
                <Route path="/thuisbatterij-calculator" element={<ThuisbatterijCalculator />} />
                <Route path="/thuisbatterijen" element={<Thuisbatterijen />} />
                <Route path="/kennisbank/thuisbatterijen" element={<KnowledgeBaseThuisbatterijen />} />

                {/* SEO Articles */}
                <Route path="/kennisbank/aircospecialist-limburg" element={<AircoSpecialistLimburg />} />
                <Route path="/kennisbank/airco-venlo" element={<AircoVenlo />} />
                <Route path="/kennisbank/airco-weert" element={<AircoWeert />} />
                <Route path="/kennisbank/airco-service-limburg" element={<AircoServiceLimburg />} />
                <Route path="/kennisbank/airco-installatie-limburg" element={<AircoInstallatieLimburg />} />
                <Route path="/kennisbank/airco-installateur-limburg" element={<AircoInstallateurLimburg />} />
                <Route path="/kennisbank/klimaatbeheersing" element={<Klimaatbeheersing />} />
                <Route path="/kennisbank/scop-4-6" element={<Scop46 />} />
                <Route path="/kennisbank/airco-onderhoud-limburg" element={<AircoOnderhoudLimburg />} />
                <Route path="/kennisbank/airco-installeren-limburg" element={<AircoInstallerenLimburg />} />
                <Route path="/kennisbank/airco-installateur-roermond" element={<AircoInstallateurRoermond />} />
                <Route path="/kennisbank/airco-installateur-maastricht" element={<AircoInstallateurMaastricht />} />
                <Route path="/kennisbank/airco-brunssum" element={<AircoBrunssum />} />
                <Route path="/kennisbank/airco-sittard" element={<AircoSittard />} />
                <Route path="/kennisbank/mitsubishi-airco-limburg" element={<MitsubishiAircoLimburg />} />
                <Route path="/kennisbank/airconditioning-kerkrade" element={<AirconditioningKerkrade />} />
                <Route path="/kennisbank/airco-plaatsen-limburg" element={<Navigate to="/airco-plaatsen-limburg" replace />} />
                <Route path="/kennisbank/energiezuinige-airco-limburg" element={<EnergiezuinigeAircoLimburg />} />
                <Route path="/airco-verwarming-kosten-besparing" element={<AircoVerwarmingKostenBesparing />} />
                <Route path="/kennisbank/airco-verwarming-voordelen" element={<AircoVerwarmingVoordelen />} />
                <Route path="/kosten-airco-plaatsen" element={<KostenAircoPlaatsen />} />

                {/* Landing Pages */}
                <Route path="/landing/daikin" element={<DaikinLanding />} />
                <Route path="/landing/lg" element={<LGLanding />} />
                <Route path="/landing/mitsubishi-heavy" element={<MitsubishiHeavyLanding />} />
                <Route path="/landing/toshiba" element={<ToshibaLanding />} />
                <Route path="/landing/tosot" element={<TosotLanding />} />
                <Route path="/mobiele-airco" element={<MobieleAircoLanding />} />
                <Route path="/airco-met-buitenunit" element={<AircoMetBuitenunitLanding />} />
                <Route path="/airco-installatie" element={<AircoInstallatieLanding />} />
                <Route path="/airco-installateur" element={<AircoInstallateurLanding />} />
                <Route path="/airco-onderhoud" element={<AircoOnderhoudLanding />} />

                {/* Cluster 1 — Airco bijvullen & service */}
                <Route path="/airco-bijvullen" element={<AircoBijvullen />} />
                <Route path="/airco-vullen-kosten" element={<AircoVullenKosten />} />
                <Route path="/kennisbank/airco-bijvullen-zelf-doen" element={<AircoBijvullenZelfDoen />} />
                <Route path="/airco-service" element={<AircoService />} />

                {/* Cluster 4 — Limburg uitbreiding (geo-cluster) */}
                <Route path="/airco-plaatsen-limburg" element={<AircoPlaatsenLimburg />} />

                {/* Cluster 3+4 — Subsidie & airco-warmtepomp */}
                <Route path="/airco-subsidie" element={<AircoSubsidie />} />
                <Route path="/isde-aanvragen-airco" element={<IsdeAanvragenAirco />} />
                <Route path="/airco-warmtepomp" element={<AircoWarmtepomp />} />
                <Route path="/hybride-warmtepomp-airco" element={<HybrideWarmtepompAirco />} />
                <Route path="/onderhoud-email" element={<OnderhoudLanding />} />
                <Route path="/airco-reparatie" element={<AircoReparatieLanding />} />
                <Route path="/airco-laten-plaatsen" element={<AircoLatenPlaatsenLanding />} />
                <Route path="/wat-kost-airco-installatie" element={<WatKostAircoInstallatieLanding />} />
                <Route path="/airco-service-limburg" element={<AircoServiceLimburgLanding />} />
                <Route path="/airco-kopen-limburg" element={<AircoKopenLimburg />} />
                <Route path="/airco-kopen-en-laten-installeren" element={<AircoKopenEnLatenInstalleren />} />
                <Route path="/airco-voor-slaapkamer" element={<AircoVoorSlaapkamerLanding />} />
                <Route path="/airco-voor-woonkamer" element={<AircoVoorWoonkamerLanding />} />
                <Route path="/airco-voor-zolder" element={<AircoVoorZolderLanding />} />

                {/* Location Pages */}
                {/* All city pages route through one catch-all → ProgrammaticLocationPage. */}
                <Route path="/airco-installatie/:citySlug" element={<ProgrammaticLocationPage />} />

                {/* Blog Pages */}
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/airco-onderhoud-limburg-belangrijk" element={<AircoOnderhoudLimburgBelangrijk />} />
                <Route path="/blog/airco-installateur-limburg-kiezen" element={<AircoInstallateurLimburgKiezen />} />
                <Route path="/blog/airco-offerte-limburg" element={<AircoOfferteLimburg />} />
                <Route path="/blog/airco-specialist-limburg" element={<AircoSpecialistLimburgBlog />} />
                <Route path="/blog/airco-installatie-zuid-limburg" element={<AircoInstallatieZuidLimburg />} />
                <Route path="/blog/wat-kost-1-uur-airco-draaien" element={<WatKostEenUurAircoDraaien />} />
                <Route path="/blog/airco-subsidie-2026-limburg" element={<AircoSubsidie2026Limburg />} />
                <Route path="/blog/beste-airco-merken-2026" element={<BesteAircoMerken2026 />} />
                <Route path="/blog/airco-maastricht-complete-gids-2026" element={<AircoMaastrichtCompleteGids2026 />} />
                <Route path="/blog/airco-levensduur-wanneer-vervangen" element={<AircoLevensduurWanneerVervangen />} />
                <Route path="/blog/airco-storingen-herkennen" element={<AircoStoringenHerkennen />} />
                <Route path="/blog/airco-offerte-maastricht" element={<AircoOfferteMaastricht />} />
                <Route path="/blog/airco-offerte-heerlen" element={<AircoOfferteHeerlen />} />
                <Route path="/blog/airco-offerte-sittard" element={<AircoOfferteSittard />} />
                <Route path="/blog/airco-offerte-aanvragen-checklist" element={<AircoOfferteAanvragenChecklist />} />
                <Route path="/blog/airco-offerte-online-vs-inmeting" element={<AircoOfferteOnlineVsInmeting />} />
                <Route path="/blog/waarom-verschillen-airco-offertes" element={<WaaromVerschillenAircoOffertes />} />
                <Route path="/blog/multisplit-airco-offerte-wanneer-rendabel" element={<MultisplitAircoOfferteWanneerRendabel />} />

                {/* SEO Advice Pages */}
                <Route path="/advies" element={<AdviesIndex />} />
                <Route path="/advies/beste-airco-slaapkamer" element={<BesteAircoSlaapkamer />} />
                <Route path="/advies/top-10-stille-aircos" element={<Top10StilleAircos />} />
                <Route path="/advies/top-5-energiezuinige-aircos" element={<Top5EnergiezuinigeAircos />} />
                <Route path="/advies/beste-multi-split-aircos" element={<BesteMultiSplitAircos />} />

                {/* SEO Landing Pages - Pillar 1: Installatie Services */}
                <Route path="/seo/pillar-1-installatie-services/airco-installatie-woonkamer" element={<SEOAircoInstallatieWoonkamer />} />
                <Route path="/seo/pillar-1-installatie-services/airco-installatie-slaapkamer" element={<SEOAircoInstallatieSlaapkamer />} />
                <Route path="/seo/pillar-1-installatie-services/airco-installatie-kantoor" element={<SEOAircoInstallatieKantoor />} />
                <Route path="/seo/pillar-1-installatie-services/airco-installatie-bedrijfspand" element={<SEOAircoInstallatieBedrijfspand />} />
                <Route path="/seo/pillar-1-installatie-services/multi-split-airco-installatie" element={<SEOMultiSplitAircoInstallatie />} />

                {/* SEO Landing Pages - Pillar 2: Onderhoud & Service */}
                <Route path="/seo/pillar-2-onderhoud-service/jaarlijks-airco-onderhoud" element={<SEOJaarlijksAircoOnderhoud />} />
                <Route path="/seo/pillar-2-onderhoud-service/airco-reparatie-service" element={<SEOAircoReparatieService />} />
                <Route path="/seo/pillar-2-onderhoud-service/airco-filter-reiniging" element={<SEOAircoFilterReiniging />} />
                {/* 301-style redirect: oude SEO-pillar slug verwijst naar nieuwe top-level pillar */}
                <Route path="/seo/pillar-2-onderhoud-service/airco-koudemiddel-bijvullen" element={<Navigate to="/airco-bijvullen" replace />} />
                <Route path="/seo/pillar-2-onderhoud-service/airco-storing-oplossen" element={<SEOAircoStoringOplossen />} />

                {/* SEO Landing Pages - Pillar 3: Merken & Modellen */}
                <Route path="/seo/pillar-3-merken-modellen/daikin-airco-modellen" element={<SEODaikinAircoModellen />} />
                <Route path="/seo/pillar-3-merken-modellen/mitsubishi-heavy-airco" element={<SEOMitsubishiHeavyAirco />} />
                <Route path="/seo/pillar-3-merken-modellen/lg-airco-systemen" element={<SEOLGAircoSystemen />} />
                <Route path="/seo/pillar-3-merken-modellen/tosot-gree-airco" element={<SEOTosotGreeAirco />} />
                <Route path="/seo/pillar-3-merken-modellen/samsung-airco" element={<SEOSamsungAirco />} />

                {/* SEO Landing Pages - Pillar 4: Energie Besparen */}
                <Route path="/seo/pillar-4-energie-besparen/verwarmen-met-airco" element={<SEOVerwarmenMetAirco />} />
                <Route path="/seo/pillar-4-energie-besparen/terugleverkosten-voorkomen" element={<SEOTerugleverkostenVoorkomen />} />
                <Route path="/seo/pillar-4-energie-besparen/scop-waarde-uitgelegd" element={<SEOSCOPWaardeUitgelegd />} />
                <Route path="/seo/pillar-4-energie-besparen/energielabel-airco" element={<SEOEnergielabelAirco />} />
                <Route path="/seo/pillar-4-energie-besparen/stroomverbruik-airco" element={<SEOStroomverbruikAirco />} />

                {/* SEO Landing Pages - Pillar 5: AlphaESS Thuisbatterijen */}
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-smile-b3" element={<SEOThuisbatterijSMILEB3 />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-smile5" element={<SEOThuisbatterijSMILE5 />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-smile-t10" element={<SEOThuisbatterijSMILET10 />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-btw-teruggave" element={<SEOThuisbatterijBTWTeruggave />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-terugverdientijd" element={<SEOThuisbatterijTerugverdientijd />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-alphaess-vs-concurrenten" element={<SEOThuisbatterijAlphaESSVsConcurrenten />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-kopen-of-huren" element={<SEOThuisbatterijKopenOfHuren />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-dynamisch-energiecontract" element={<SEOThuisbatterijDynamischEnergiecontract />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-zonnepanelen" element={<SEOThuisbatterijZonnepanelen />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-installatie-limburg" element={<SEOThuisbatterijInstallatieLimburg />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-1-fase-vs-3-fase" element={<SEOThuisbatterij1FaseVs3Fase />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-capaciteit-kiezen" element={<SEOThuisbatterijCapaciteitKiezen />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-kosten-overzicht" element={<SEOThuisbatterijKostenOverzicht />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-subsidie-limburg" element={<SEOThuisbatterijSubsidieLimburg />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-noodstroom" element={<SEOThuisbatterijNoodstroom />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-elektrische-auto" element={<SEOThuisbatterijElektrischeAuto />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-onderhoud" element={<SEOThuisbatterijOnderhoud />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-levensduur-garantie" element={<SEOThuisbatterijLevensduurGarantie />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-warmtepomp" element={<SEOThuisbatterijWarmtepomp />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-smart-home-integratie" element={<SEOThuisbatterijSmartHome />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-nieuwbouw" element={<SEOThuisbatterijNieuwbouw />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-saldering-2027" element={<SEOThuisbatterijSaldering2027 />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-zakelijk-mkb" element={<SEOThuisbatterijZakelijkMKB />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-monitoring-app" element={<SEOThuisbatterijMonitoringApp />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-verzekering" element={<SEOThuisbatterijVerzekering />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-financieren" element={<SEOThuisbatterijFinancieren />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-aansluiten-meterkast" element={<SEOThuisbatterijAansluitenMeterkast />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-besparing-berekenen" element={<SEOThuisbatterijBesparingBerekenen />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-huren-vs-kopen" element={<SEOThuisbatterijHurenVsKopen />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-subsidie-2026" element={<SEOThuisbatterijSubsidie2026 />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-vs-tesla-powerwall" element={<SEOThuisbatterijVsTeslaPowerwall />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-vs-waterstof" element={<SEOThuisbatterijVsWaterstof />} />
                <Route path="/seo/pillar-5-thuisbatterijen/alphaess-vs-huawei-luna" element={<SEOAlphaESSVsHuaweiLuna />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-maastricht" element={<SEOThuisbatterijMaastricht />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-roermond" element={<SEOThuisbatterijRoermond />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-heerlen" element={<SEOThuisbatterijHeerlen />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-venlo" element={<SEOThuisbatterijVenlo />} />
                <Route path="/seo/pillar-5-thuisbatterijen/thuisbatterij-sittard-geleen" element={<SEOThuisbatterijSittardGeleen />} />

                {/* Noindex thuisbatterij pages */}
                <Route path="/thuisbatterijen/noindex/ervaringen-klanten" element={<SEOThuisbatterijErvaringenKlanten />} />
                <Route path="/thuisbatterijen/noindex/gids-limburg" element={<SEOThuisbatterijGidsLimburg />} />
                <Route path="/thuisbatterijen/noindex/hulp-kiezen" element={<SEOThuisbatterijHulpKiezen />} />
                <Route path="/thuisbatterijen/noindex/installateurs-regio" element={<SEOThuisbatterijInstallateursRegio />} />
                <Route path="/thuisbatterijen/noindex/installeren-stappen" element={<SEOThuisbatterijInstallerenStappen />} />
                <Route path="/thuisbatterijen/noindex/lokale-regelgeving" element={<SEOThuisbatterijLokaleRegelgeving />} />
                <Route path="/thuisbatterijen/noindex/merken-vergelijken" element={<SEOThuisbatterijMerkenVergelijken />} />
                <Route path="/thuisbatterijen/noindex/prijzen-2025" element={<SEOThuisbatterijPrijzen2025 />} />
                <Route path="/thuisbatterijen/noindex/veelgestelde-vragen" element={<SEOThuisbatterijVeelgesteldeVragen />} />
                <Route path="/thuisbatterijen/noindex/voor-huurders" element={<SEOThuisbatterijVoorHuurders />} />
                <Route path="/thuisbatterijen/noindex/voor-senioren" element={<SEOThuisbatterijVoorSenioren />} />
                <Route path="/thuisbatterijen/noindex/voor-zelfstandigen" element={<SEOThuisbatterijVoorZelfstandigen />} />
                <Route path="/thuisbatterijen/noindex/vragen-antwoorden-limburg" element={<SEOThuisbatterijVragenAntwoordenLimburg />} />
                <Route path="/thuisbatterijen/noindex/wanneer-rendabel" element={<SEOThuisbatterijWanneerRendabel />} />
                <Route path="/thuisbatterijen/noindex/werking-uitgelegd" element={<SEOThuisbatterijWerkingUitgelegd />} />

                {/* Noindex airco installatie limburg pages */}
                <Route path="/airco-installatie/noindex/stappenplan-limburg" element={<SEOAircoInstallatieLimburgStappenplan />} />
                <Route path="/airco-installatie/noindex/kosten-limburg" element={<SEOAircoInstallatieLimburgKosten />} />
                <Route path="/airco-installatie/noindex/faq-limburg" element={<SEOAircoInstallatieLimburgFAQ />} />

                {/* Noindex airco onderhoud pages */}
                <Route path="/airco-onderhoud/noindex/checklist" element={<SEOAircoOnderhoudChecklist />} />
                <Route path="/airco-onderhoud/noindex/seizoenen" element={<SEOAircoOnderhoudSeizoenen />} />
                <Route path="/airco-onderhoud/noindex/problemen-voorkomen" element={<SEOAircoOnderhoudProblemenVoorkomen />} />

                {/* Noindex airco reparatie pages */}
                <Route path="/airco-reparatie/noindex/storingen" element={<SEOAircoReparatieStoringen />} />
                <Route path="/airco-reparatie/noindex/kosten-limburg" element={<SEOAircoReparatieKostenLimburg />} />
                <Route path="/airco-reparatie/noindex/spoed" element={<SEOAircoReparatieSpoed />} />

                {/* Noindex airco service limburg pages */}

                {/* Noindex airco merken pages */}
                <Route path="/airco-merken/noindex/keuzehulp" element={<SEOMerkenKeuzehulp />} />
                <Route path="/airco-merken/noindex/modellen-2025" element={<SEOModellenOverzicht2025 />} />
                <Route path="/airco-merken/noindex/vergelijking-tips" element={<SEOMerkVergelijkingTips />} />
                <Route path="/airco-service/noindex/wat-verwachten" element={<SEOAircoServiceWatVerwachten />} />
                <Route path="/airco-service/noindex/contract-voordelen" element={<SEOAircoServiceContractVoordelen />} />
                <Route path="/airco-service/noindex/limburg-regio" element={<SEOAircoServiceLimburgRegio />} />

                {/* Noindex energiezuinige airco pages */}
                <Route path="/energiezuinig/noindex/kiezen" element={<SEOEnergiezuinigeAircoKiezen />} />
                <Route path="/energiezuinig/noindex/energielabel" element={<SEOAircoEnergielabelUitgelegd />} />
                <Route path="/energiezuinig/noindex/stroomverbruik" element={<SEOAircoStroomverbruikBerekenen />} />

                {/* SEO Landing Pages - Pillar 6: Limburg Steden */}
                <Route path="/seo/pillar-6-limburg-steden/airco-installatie-maastricht" element={<SEOAircoInstallatieMaastricht />} />
                <Route path="/seo/pillar-6-limburg-steden/airco-installatie-roermond" element={<SEOAircoInstallatieRoermond />} />
                <Route path="/seo/pillar-6-limburg-steden/airco-service-heerlen" element={<SEOAircoServiceHeerlen />} />
                <Route path="/seo/pillar-6-limburg-steden/airco-monteur-sittard-geleen" element={<SEOAircoMonteurSittardGeleen />} />
                <Route path="/seo/pillar-6-limburg-steden/airco-plaatsen-venlo" element={<SEOAircoPlaatsenVenlo />} />
                <Route path="/seo/pillar-6-limburg-steden/airco-installatie-weert" element={<SEOAircoInstallatieWeert />} />
                <Route path="/seo/pillar-6-limburg-steden/airco-installatie-kerkrade" element={<SEOAircoInstallatieKerkrade />} />
                <Route path="/seo/pillar-6-limburg-steden/airco-installatie-echt-susteren" element={<SEOAircoInstallatieEchtSusteren />} />

                {/* Noindex Limburg Steden pages */}
                <Route path="/airco-limburg/noindex/wijkgids" element={<SEOLimburgWijkgids />} />
                <Route path="/airco-limburg/noindex/gemeenten-overzicht" element={<SEOLimburgGemeentenOverzicht />} />
                <Route path="/airco-limburg/noindex/reistijd-service" element={<SEOLimburgReistijdService />} />

                {/* SEO Landing Pages - Pillar 7: Technische Gidsen */}
                <Route path="/seo/pillar-7-technische-gidsen/airco-capaciteit-berekenen" element={<SEOAircoCapaciteitBerekenen />} />
                <Route path="/seo/pillar-7-technische-gidsen/airco-buitenunit-plaatsing" element={<SEOAircoBuitenunitPlaatsing />} />
                <Route path="/seo/pillar-7-technische-gidsen/airco-binnenunit-positie" element={<SEOAircoBinnenunitPositie />} />
                <Route path="/seo/pillar-7-technische-gidsen/airco-leidingwerk" element={<SEOAircoLeidingwerk />} />
                <Route path="/seo/pillar-7-technische-gidsen/airco-wifi-bediening" element={<SEOAircoWiFiBediening />} />

                {/* SEO Landing Pages - Pillar 7: Technische Gidsen (NOINDEX) */}
                <Route path="/airco-technisch/noindex/specificaties-uitleg" element={<SEOTechnischeSpecificaties />} />
                <Route path="/airco-technisch/noindex/capaciteit-tool" element={<SEOCapaciteitBerekeningTool />} />
                <Route path="/airco-technisch/noindex/installatie-eisen" element={<SEOInstallatieTechnischeEisen />} />

                {/* SEO Landing Pages - Pillar 8: Vergelijkingen */}
                <Route path="/seo/pillar-8-vergelijkingen/split-unit-vs-mobiele-airco" element={<SEOSplitUnitVsMobieleAirco />} />
                <Route path="/seo/pillar-8-vergelijkingen/mono-split-vs-multi-split" element={<SEOMonoSplitVsMultiSplit />} />
                <Route path="/seo/pillar-8-vergelijkingen/inverter-vs-on-off-airco" element={<SEOInverterVsOnOffAirco />} />
                <Route path="/seo/pillar-8-vergelijkingen/daikin-vs-mitsubishi" element={<SEODaikinVsMitsubishi />} />
                <Route path="/seo/pillar-8-vergelijkingen/airco-vs-ventilator" element={<SEOAircoVsVentilator />} />

                {/* SEO Landing Pages - Pillar 8: Vergelijkingen (NOINDEX) */}
                <Route path="/airco-vergelijking/noindex/types-gids" element={<SEOTypesVergelijkenGids />} />
                <Route path="/airco-vergelijking/noindex/split-vs-mobiel" element={<SEOSplitVsMobielWatKiezen />} />
                <Route path="/airco-vergelijking/noindex/checklist" element={<SEOAircoVergelijkingChecklist />} />

                {/* SEO Landing Pages - Pillar 9: Kosten & Prijzen */}
                <Route path="/seo/pillar-9-kosten-prijzen/kosten-airco-plaatsen" element={<SEOKostenAircoPlaatsen />} />
                <Route path="/seo/pillar-9-kosten-prijzen/airco-subsidies-limburg" element={<SEOAircoSubsidiesLimburg />} />
                <Route path="/seo/pillar-9-kosten-prijzen/airco-financiering" element={<SEOAircoFinanciering />} />
                <Route path="/seo/pillar-9-kosten-prijzen/airco-onderhoud-kosten" element={<SEOAircoOnderhoudKosten />} />
                <Route path="/seo/pillar-9-kosten-prijzen/airco-verbruikskosten" element={<SEOAircoVerbruikskosten />} />

                {/* SEO Landing Pages - Pillar 9: Kosten & Prijzen (NOINDEX) */}
                <Route path="/airco-kosten/noindex/airco-prijzen-vergelijken" element={<SEOAircoPrijzenVergelijken />} />
                <Route path="/airco-kosten/noindex/airco-kosten-besparen-tips" element={<SEOAircoKostenBesparenTips />} />
                <Route path="/airco-kosten/noindex/airco-investering-terugverdienen" element={<SEOAircoInvesteringTerugverdienen />} />

                {/* SEO Landing Pages - Pillar 10: Duurzaamheid & Milieu */}
                <Route path="/seo/pillar-10-duurzaamheid/koudemiddel-r32" element={<SEOKoudemiddelR32 />} />
                <Route path="/seo/pillar-10-duurzaamheid/co2-reductie-warmtepomp" element={<SEOCO2ReductieWarmtepomp />} />
                <Route path="/seo/pillar-10-duurzaamheid/duurzame-verwarming" element={<SEODuurzameVerwarming />} />
                <Route path="/seo/pillar-10-duurzaamheid/energielabel-verbetering" element={<SEOEnergielabelVerbetering />} />
                <Route path="/seo/pillar-10-duurzaamheid/circulaire-airco" element={<SEOCirculaireAirco />} />

                {/* SEO Landing Pages - Pillar 10: Duurzaamheid & Milieu (NOINDEX) */}
                <Route path="/duurzaamheid/noindex/airco-keuze-gids" element={<SEODuurzameAircoGids />} />
                <Route path="/duurzaamheid/noindex/milieuvriendelijke-koeling-tips" element={<SEOMilieuvriendelijkeKoelingTips />} />
                <Route path="/duurzaamheid/noindex/airco-co2-footprint-verlagen" element={<SEOAircoCO2FootprintVerlagen />} />

                {/* Data-driven high-intent service pages */}
                {servicePages.map((servicePage) => (
                  <Route
                    key={servicePage.slug}
                    path={`/${servicePage.slug}`}
                    element={<ServiceLandingPage slug={servicePage.slug} />}
                  />
                ))}

                {/* New SEO Funnel - BOFU Money Pages */}
                <Route path="/airco-installatie-prijs" element={<AircoInstallatiePrijs />} />
                <Route path="/airco-offerte" element={<AircoOfferte />} />
                <Route path="/split-airco-installatie" element={<SplitAircoInstallatie />} />
                <Route path="/multi-split-airco-installatie" element={<MultiSplitAircoInstallatie />} />

                {/* New SEO Funnel - MOFU Comparison/Decision Pages */}
                <Route path="/beste-airco-voor-verwarmen" element={<BesteAircoVoorVerwarmen />} />
                <Route path="/airco-3-5kw-hoeveel-m2" element={<Airco35KwHoevelM2 />} />
                <Route path="/airco-slaapkamer-welke-kw" element={<AircoSlaapkamerWelkeKw />} />
                <Route path="/airco-koelen-en-verwarmen" element={<AircoKoelenEnVerwarmen />} />
                <Route path="/airco-met-wifi-voordelen" element={<AircoMetWifiVoordelen />} />
                <Route path="/airco-a-plus-plus-plus-verwarmen" element={<AircoAPlusPlusVerwarmen />} />
                <Route path="/airco-met-buitenunit-of-zonder" element={<AircoMetBuitenunitOfZonder />} />
                <Route path="/beste-airco-merken" element={<BesteAircoMerken />} />
                <Route path="/airconditioning" element={<AirconditioningPillar />} />
                <Route path="/lg-vs-daikin-airco" element={<LGVsDaikinAirco />} />

                {/* New SEO Funnel - Room Landing Pages */}
                <Route path="/airco-kantoor" element={<AircoKantoor />} />
                <Route path="/airco-winkel" element={<AircoWinkel />} />

                {/* New SEO Funnel - TOFU Articles */}
                <Route path="/airco-als-verwarming-kosten" element={<AircoAlsVerwarmingKosten />} />
                <Route path="/wat-kost-airco-per-maand" element={<WatKostAircoPerMaand />} />
                <Route path="/is-airco-goedkoper-dan-gas" element={<IsAircoGoedkoperDanGas />} />
                <Route path="/airco-geluid-buren-regels" element={<AircoGeluidBurenRegels />} />
                <Route path="/airco-vs-warmtepomp" element={<AircoVsWarmtepomp />} />
                <Route path="/airco-voordelen-en-nadelen" element={<AircoVoordelenEnNadelen />} />
                <Route path="/airco-installatie-duur" element={<AircoInstallatieDuur />} />
                <Route path="/airco-installeren" element={<AircoInstallerenGids />} />

                {/* Programmatic Ruimte × Stad (135 pages: 5 rooms × 27 cities) */}
                <Route path="/airco/:ruimte/:city" element={<ProgrammaticRuimteStadPage />} />

                {/* 404 Page - Catch all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              <RouteReady />
            </Suspense>
          </AnimatePresence>
          {!isBareLayout && <ContentShareSlot />}
        </main>
        {!isBareLayout && <BackToTop />}
        {!isBareLayout && <Chatbot />}
        {!isBareLayout && <Footer />}

        {/* CTR Enhancement Components — uit op naked /lp/ pagina's (geen leaks) */}
        {!isBareLayout && (
          <>
            <ExitIntentPopup />

            <FloatingReviewBadge />
            <FeedbackRibbon />
          </>
        )}

        {/* Tijdelijke service-melding (telefoon minder bereikbaar) — óók op /lp/,
            want juist daar is bellen vaak de hoofd-CTA. Zet uit via ENABLED in het component. */}
        {isBareLayout && <div className="mx-auto max-w-3xl px-4"><ServiceNoticePopup /></div>}

        <CookieConsentLite />
        <PerformanceMonitor />
      </div>
    </ErrorBoundary>
  );
};

export default React.memo(App);

// A route is interactive only after its lazy component has hydrated or mounted.
function RouteReady() {
  const location = useLocation();
  useEffect(() => { document.documentElement.dataset.routeReady = location.pathname; }, [location.pathname]);
  return null;
}
